/**
 * ImpersonationContext — RW-187 Super Admin "Login As" client-side impersonation.
 *
 * This is NOT a real Firebase auth switch. The super admin's actual auth token
 * stays active for security; we only override the user object that read-only
 * UI components consume. Firestore writes and Cloud Function calls continue to
 * use the real `auth.currentUser` so security rules and audit trails are
 * unaffected.
 *
 * Usage:
 *   const { user, isImpersonating } = useEffectiveUser();
 *
 * Storage:
 *   sessionStorage('impersonating_uid') survives page refreshes but not
 *   tab close. The full impersonated user doc is fetched from Firestore on
 *   rehydrate so we always reflect the latest profile/role.
 *
 * Guardrails:
 *   - Only super_admin can call startImpersonation()
 *   - Cannot impersonate another super_admin
 *   - Start/stop events are logged to `adminLogs` for audit
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { db, auth } from '../firebase';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { withTenant } from '../services/data';
import { setViewAsUid } from '../services/viewAs';

const STORAGE_KEY = 'impersonating_uid';

const ImpersonationContext = createContext({
  impersonatedUser: null,
  isImpersonating: false,
  startImpersonation: () => {},
  stopImpersonation: () => {},
  effectiveUser: null,
});

async function logAuditEvent(action, superAdminUid, superAdminEmail, target) {
  try {
    await addDoc(collection(db, 'adminLogs'), withTenant('adminLogs', {
      action,
      performedBy: superAdminUid,
      performedByName: superAdminEmail || '',
      targetId: target?.uid || target?.id || '',
      targetType: 'user',
      details: {
        targetEmail: target?.email || '',
        targetName: target?.displayName || '',
        targetRole: target?.role || '',
      },
      timestamp: serverTimestamp(),
    }));
  } catch (e) {
    // Audit logging is fire-and-forget — never block the UX.
    if (typeof console !== 'undefined') console.warn('impersonation audit log failed', e?.message);
  }
}

export function ImpersonationProvider({ children }) {
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  // Mirror the impersonated uid into the service-layer bridge so callables
  // (contactsApi) can re-scope reads to the viewed user's book. Covers start,
  // stop, AND sessionStorage rehydrate (all paths set impersonatedUser).
  useEffect(() => {
    setViewAsUid(impersonatedUser?.uid || null);
  }, [impersonatedUser]);

  // Rehydrate on mount — sessionStorage survives refreshes within a tab.
  useEffect(() => {
    let cancelled = false;
    let storedUid = null;
    try { storedUid = sessionStorage.getItem(STORAGE_KEY); } catch { /* noop */ }
    if (!storedUid) return undefined;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', storedUid));
        if (cancelled) return;
        if (snap.exists()) {
          setImpersonatedUser({ uid: snap.id, ...snap.data() });
        } else {
          try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
        }
      } catch {
        // If we can't load the doc, drop the impersonation rather than render
        // an inconsistent view.
        try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startImpersonation = useCallback(async (targetUser, callerRole) => {
    if (!targetUser?.uid && !targetUser?.id) return false;
    // Hard guard — only super_admin can impersonate.
    if (callerRole !== 'super_admin') {
      if (typeof console !== 'undefined') console.warn('startImpersonation blocked: caller is not super_admin');
      return false;
    }
    // Cannot impersonate another super_admin.
    if (targetUser.role === 'super_admin') {
      if (typeof console !== 'undefined') console.warn('startImpersonation blocked: cannot impersonate another super_admin');
      return false;
    }
    const normalized = { uid: targetUser.uid || targetUser.id, ...targetUser };
    setImpersonatedUser(normalized);
    try { sessionStorage.setItem(STORAGE_KEY, normalized.uid); } catch { /* noop */ }
    // Audit log — fire and forget. The real auth user is the actor.
    const realUser = auth.currentUser;
    logAuditEvent('impersonation_start', realUser?.uid, realUser?.email, normalized);
    return true;
  }, []);

  const stopImpersonation = useCallback(() => {
    const target = impersonatedUser;
    setImpersonatedUser(null);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    if (target) {
      const realUser = auth.currentUser;
      logAuditEvent('impersonation_stop', realUser?.uid, realUser?.email, target);
    }
  }, [impersonatedUser]);

  const value = useMemo(() => ({
    impersonatedUser,
    isImpersonating: !!impersonatedUser,
    startImpersonation,
    stopImpersonation,
    effectiveUser: impersonatedUser, // null when not impersonating; consumers fall back to real user
  }), [impersonatedUser, startImpersonation, stopImpersonation]);

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}
